import { useQuery } from 'react-query'

import queryClient from '../queryClient'
import { request } from '../sharedLegacy/utils/api'
import { type APIResponse, HTTPMethod } from '../sharedLegacy/utils/api.types'

type SettingsSpecifications = SpecificationFormat & {
  webinars: {
    screenShareContentHint: {
      name: 'optimize_webinar_screenshare_content'
      type: string
      has_options: false
    }
    locale: {
      name: 'locale'
      type: string
      has_options: false
    }
  }
}

// ///////////////////////////////////////////////////////////////
// ///////////////////////////////////////////////////////////////
// ///////////////////////////////////////////////////////////////

type DeepPartial<T> = T extends object
  ? { [K in keyof T]?: DeepPartial<T[K]> }
  : T

type DeepNoExcess<T, U> = {
  [K in keyof U]: K extends keyof T ? DeepNoExcess<Required<T>[K], U[K]> : never
}
type CompliesWith<U extends DeepPartial<T> & DeepNoExcess<T, U>, T> = U

type SpecificationFormat = Record<
  string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Record<string, { name: string; type: any; has_options: boolean }>
>

/** This is a type check that ensures the SettingsSpecification above has the correct format */
// @ts-expect-error — see above
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _EnsureSpecificationFormat = CompliesWith<
  SettingsSpecifications,
  SpecificationFormat
>

// ///////////////////////////////////////////////////////////////
// ///////////////////////////////////////////////////////////////
// ///////////////////////////////////////////////////////////////

enum CustomTypes {
  StringArray = '--StringArray--',
  BooleanRecord = '--BooleanRecord--',
}

type DTOTypeTransform<T> = T extends boolean
  ? 0 | 1
  : T extends CustomTypes.StringArray
    ? string
    : T extends CustomTypes.BooleanRecord
      ? string
      : T

type ModelTypeTransform<T> = T extends CustomTypes.StringArray
  ? string[]
  : T extends CustomTypes.BooleanRecord
    ? Record<string, boolean>
    : T

type Entry<O> = O[keyof O]

type FlatSettingsSpecs = {
  [SettingName in keyof SettingsSpecifications['webinars'] as SettingsSpecifications['webinars'][SettingName]['name']]: SettingsSpecifications['webinars'][SettingName]
}

// Settings come as an array from api
type SettingsDTOArray = Entry<{
  [SettingName in keyof FlatSettingsSpecs]: {
    setting: FlatSettingsSpecs[SettingName]['name']
    update_p: boolean
    value: DTOTypeTransform<FlatSettingsSpecs[SettingName]['type']>
    options: FlatSettingsSpecs[SettingName]['has_options'] extends true
      ? {
          value: DTOTypeTransform<FlatSettingsSpecs[SettingName]['type']>
          label: string
        }[]
      : never
  }
}>[]

// Settings as used to update api
type SettingsDTO = {
  [SettingName in keyof FlatSettingsSpecs as FlatSettingsSpecs[SettingName]['name']]: DTOTypeTransform<
    FlatSettingsSpecs[SettingName]['type']
  >
}

// Settings in their simple form just with their corresponding value (use for reading)
type Settings = {
  [SettingGroup in keyof SettingsSpecifications]: {
    [SettingName in keyof SettingsSpecifications[SettingGroup]]: SettingsSpecifications[SettingGroup][SettingName] extends {
      type: infer Type
    }
      ? ModelTypeTransform<Type>
      : never
  }
}

// ///////////////////////////////////////////////////////////////
// ///////////////////////////////////////////////////////////////
// ///////////////////////////////////////////////////////////////

const mapDTOToSettings = (apiSettings: SettingsDTOArray): Settings => {
  const dto = apiSettings.reduce<SettingsDTO>(
    (settingsObject, settingEntry) => {
      return { ...settingsObject, [settingEntry.setting]: settingEntry.value }
    },
    {} as SettingsDTO
  )

  return {
    webinars: {
      screenShareContentHint: dto.optimize_webinar_screenshare_content,
      locale: dto.locale,
    },
  }
}

const SETTINGS_QUERY_KEY = ['settings']

const fetchSettings = () =>
  request<never, APIResponse & { settings: SettingsDTOArray }>({
    url: '/api/setting/list',
    method: HTTPMethod.GET,
  })

const settingsQuery = {
  queryKey: SETTINGS_QUERY_KEY,
  queryFn: async (): Promise<Settings> => {
    const data = await fetchSettings()
    return mapDTOToSettings(data.settings)
  },
  keepPreviousData: true,
  staleTime: 10 * 60 * 1000, // 10 mins
  cacheTime: 10 * 60 * 1000, // 10 mins
}

const useSettingsQuery = () => {
  return useQuery(settingsQuery)
}

const getSettings = async () => {
  return queryClient.fetchQuery(settingsQuery)
}

export { useSettingsQuery, getSettings, type Settings }
